import type { Color } from '../../../types/colors';

interface GetItemStyleParams {
  isSelected: boolean;
  isKeyboardHighlighted?: boolean;
  color: Color;
  isDisabled?: boolean;
}

/**
 * ITEM STYLE COMPUTATION UTILITY
 * ==============================
 *
 * Manages the complex color override system for dropdown items.
 * This function implements the style priority hierarchy documented in DropdownItemButton.tsx:
 *
 * STYLE PRIORITY (highest to lowest):
 * 1. Disabled state - overrides all other styles
 * 2. Selected + keyboard highlighted - special case for info color
 * 3. Keyboard highlighted - accessibility focus styles
 * 4. Default/hover - handled by CSS classes
 *
 * COLOR THEMING STRATEGY:
 * - Uses CSS custom properties (--bs-*) for theme integration
 * - Applies !important for specific overrides when needed
 * - Maintains proper contrast ratios for accessibility
 * - Supports all theme colors via the Color type
 *
 * @param isSelected - Whether the item is currently selected
 * @param isKeyboardHighlighted - Whether keyboard navigation is highlighting this item
 * @param color - Theme color (primary, secondary, info, success, etc.)
 * @param isDisabled - Whether the item is disabled
 * @returns CSS properties object for inline styling
 */
export function getItemStyle({
  isSelected,
  isKeyboardHighlighted = false,
  color,
  isDisabled = false,
}: GetItemStyleParams) {
  // Priority 1: Disabled state overrides everything
  if (isDisabled) {
    return {
      backgroundColor: 'transparent',
      opacity: '0.4',
    };
  }

  // Priority 2: Special case for selected + keyboard highlighted info items
  if (isSelected && isKeyboardHighlighted && color === 'info') {
    return { backgroundColor: 'rgba(var(--bs-info-rgb, var(--info-rgb)), 0.13) !important' };
  }

  // Priority 3: Keyboard highlight styles for accessibility
  if (isKeyboardHighlighted) {
    return {
      color: 'var(--bs-btn-hover-color)',
      backgroundColor: 'var(--bs-btn-hover-bg) !important',
      // Uncomment for focus ring: boxShadow: "inset 0 0 0 .125rem var(--bs-btn-focus-ring-color) !important",
    };
  }

  // Priority 4: Default styles (handled by CSS classes)
  return {};
}

interface GetBadgeStyleParams {
  size: 'sm' | 'md' | 'lg' | 'xl';
}

/**
 * BADGE STYLE COMPUTATION UTILITY
 * ===============================
 *
 * Computes size-specific styles for selection badges in multiple selection mode.
 * Uses CSS custom properties for consistent theming across sizes.
 *
 * @param size - Badge size variant
 * @returns Object with button and icon style properties
 */
export function getBadgeStyle({ size }: GetBadgeStyleParams) {
  const styles = {
    sm: {
      button: { width: '16px', height: '16px' },
      icon: { fontSize: 'var(--picker-icon-font-size-sm)', height: '16px' },
    },
    md: {
      button: { width: '24px', height: '24px' },
      icon: { fontSize: 'var(--picker-icon-font-size-md)', height: '18px' },
    },
    lg: {
      button: { width: '28px', height: '28px' },
      icon: { fontSize: 'var(--picker-icon-font-size-lg)', height: '22px' },
    },
    xl: {
      button: { width: '32px', height: '32px' },
      icon: { fontSize: 'var(--picker-icon-font-size-xl)', height: '24px' },
    },
  };

  return styles[size] || styles.md;
}
